import {
  Badge,
  Button,
  cn,
  ConfirmDialog,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  EmptyState,
  Input,
  Label,
  PageHeader,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Spinner,
  StatusBadge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
} from '@repo/ui'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import {
  Calendar,
  CalendarDays,
  CalendarRange,
  CheckCircle2,
  Clock,
  LinkIcon,
  List,
  MapPin,
  MoreHorizontal,
  Plus,
  Search,
  SlidersHorizontal,
  Trash2,
  User,
  XCircle,
} from 'lucide-react'
import {
  type Dispatch,
  type FormEvent,
  type SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { toast } from 'sonner'

import { AppointmentsCalendar } from '@/components/appointments/AppointmentsCalendar'
import {
  useAppointmentCounts,
  useAppointments,
  useAppointmentsInfinite,
  useCreateAppointment,
  useDeleteAppointment,
  useUpdateAppointment,
  useUpdateAppointmentStatus,
} from '@/hooks/use-appointments'
import { useAuth } from '@/hooks/use-auth'
import { useSchedulingProfiles } from '@/hooks/use-scheduling-profiles'
import { useOrgMembers } from '@/hooks/use-settings'
import { api } from '@/lib/api'
import type { PaginatedResponse } from '@/lib/api-types'
import { APPOINTMENT_STATUS_TONE } from '@/lib/status-tones'
import type {
  Appointment,
  AppointmentInput,
  AppointmentStatus,
  AppointmentTab,
  SchedulingProfile,
} from '@/types/appointments'
import type { ContactListItem } from '@/types/leads'

type AppointmentsSearch = {
  new?: boolean
  leadId?: string
}

export const Route = createFileRoute('/_app/agendamentos/')({
  validateSearch: (search: Record<string, unknown>): AppointmentsSearch => ({
    new: search.new === 'true' || search.new === true,
    leadId: typeof search.leadId === 'string' ? search.leadId : undefined,
  }),
  component: AppointmentsPage,
})

type ViewMode = 'list' | 'week' | 'month'
type StatusFilter = AppointmentStatus | 'all'

const TABS: Array<{ value: AppointmentTab; label: string }> = [
  { value: 'upcoming', label: 'Próximos' },
  { value: 'pending', label: 'Não confirmados' },
  { value: 'past', label: 'Anteriores' },
  { value: 'cancelled', label: 'Cancelados' },
  { value: 'all', label: 'Todos' },
]

const SEG_LIST_CLASS =
  'bg-card border-border inline-flex h-auto items-center gap-1 rounded-md border p-1'
const SEG_TRIGGER_CLASS =
  'flex-none rounded px-3 py-1.5 text-sm font-medium text-muted-foreground shadow-none transition-colors hover:bg-muted hover:text-foreground data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-none'

const STATUS_LABEL: Record<AppointmentStatus, string> = {
  PENDING: 'Pendente',
  REQUESTED: 'Solicitado',
  SLOT_OFFERED: 'Horário oferecido',
  PENDING_CONFIRMATION: 'Aguardando confirmação',
  CONFIRMED: 'Confirmado',
  CANCELLED: 'Cancelado',
  RESCHEDULED: 'Reagendado',
  COMPLETED: 'Concluido',
  NO_SHOW: 'Não compareceu',
  FAILED: 'Falhou',
}

const UNCONFIRMED_STATUSES: AppointmentStatus[] = [
  'PENDING',
  'REQUESTED',
  'SLOT_OFFERED',
  'PENDING_CONFIRMATION',
]

const STATUS_ACTIONS: Partial<
  Record<
    AppointmentStatus,
    Array<{
      status: AppointmentStatus
      label: string
      icon: typeof CheckCircle2
    }>
  >
> = {
  PENDING: [
    { status: 'REQUESTED', label: 'Marcar solicitado', icon: Clock },
    { status: 'CONFIRMED', label: 'Confirmar', icon: CheckCircle2 },
    { status: 'CANCELLED', label: 'Cancelar', icon: XCircle },
  ],
  REQUESTED: [
    { status: 'SLOT_OFFERED', label: 'Horário oferecido', icon: Clock },
    { status: 'CONFIRMED', label: 'Confirmar', icon: CheckCircle2 },
    { status: 'CANCELLED', label: 'Cancelar', icon: XCircle },
    { status: 'FAILED', label: 'Marcar falha', icon: XCircle },
  ],
  SLOT_OFFERED: [
    {
      status: 'PENDING_CONFIRMATION',
      label: 'Aguardar confirmação',
      icon: Clock,
    },
    { status: 'RESCHEDULED', label: 'Marcar reagendado', icon: CalendarRange },
    { status: 'CANCELLED', label: 'Cancelar', icon: XCircle },
    { status: 'FAILED', label: 'Marcar falha', icon: XCircle },
  ],
  PENDING_CONFIRMATION: [
    { status: 'CONFIRMED', label: 'Confirmar', icon: CheckCircle2 },
    { status: 'RESCHEDULED', label: 'Marcar reagendado', icon: CalendarRange },
    { status: 'CANCELLED', label: 'Cancelar', icon: XCircle },
    { status: 'FAILED', label: 'Marcar falha', icon: XCircle },
  ],
  CONFIRMED: [
    { status: 'COMPLETED', label: 'Marcar concluído', icon: CheckCircle2 },
    { status: 'NO_SHOW', label: 'Não compareceu', icon: XCircle },
    { status: 'RESCHEDULED', label: 'Marcar reagendado', icon: CalendarRange },
    { status: 'CANCELLED', label: 'Cancelar', icon: XCircle },
  ],
  RESCHEDULED: [
    { status: 'CONFIRMED', label: 'Confirmar', icon: CheckCircle2 },
    { status: 'CANCELLED', label: 'Cancelar', icon: XCircle },
    { status: 'FAILED', label: 'Marcar falha', icon: XCircle },
  ],
}

function useContacts(orgId: string | null, enabled: boolean) {
  return useQuery<ContactListItem[]>({
    queryKey: ['appointments', 'contacts', orgId],
    queryFn: async () => {
      const res = await api.get<PaginatedResponse<ContactListItem>>(
        `/orgs/${orgId}/contacts`,
        { params: { limit: 500 } }
      )
      return res.data.data
    },
    enabled: !!orgId && enabled,
    staleTime: 60_000,
  })
}

function toLocalInputValue(value: string) {
  const date = new Date(value)
  const offset = date.getTimezoneOffset() * 60_000
  return new Date(date.getTime() - offset).toISOString().slice(0, 16)
}

function formatDateTime(value: string) {
  return new Date(value).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function createDefaultDate() {
  const date = new Date()
  date.setHours(date.getHours() + 1, 0, 0, 0)
  return date
}

function emptyForm(defaultLeadId?: string | null, defaultDate?: Date | null) {
  return {
    title: '',
    schedulingProfileId: '',
    scheduled_at: toLocalInputValue(
      (defaultDate ?? createDefaultDate()).toISOString()
    ),
    duration_minutes: 30,
    leadId: defaultLeadId ?? '',
    location: '',
    meeting_url: '',
    description: '',
    status: 'REQUESTED' as AppointmentStatus,
  }
}

function getLeadLabel(appointment: Appointment) {
  return (
    appointment.lead?.name ??
    appointment.lead?.phone ??
    appointment.lead?.email ??
    appointment.leadId
  )
}

function isUpcoming(appointment: Appointment, now = Date.now()) {
  const time = new Date(appointment.scheduled_at).getTime()
  return (
    time >= now &&
    !['CANCELLED', 'COMPLETED', 'NO_SHOW', 'FAILED'].includes(
      appointment.status
    )
  )
}

function isPast(appointment: Appointment, now = Date.now()) {
  const time = new Date(appointment.scheduled_at).getTime()
  return (
    time < now ||
    ['COMPLETED', 'NO_SHOW', 'FAILED'].includes(appointment.status)
  )
}

function matchesTab(appointment: Appointment, tab: AppointmentTab) {
  const now = Date.now()
  if (tab === 'upcoming') return isUpcoming(appointment, now)
  if (tab === 'pending')
    return UNCONFIRMED_STATUSES.includes(appointment.status)
  if (tab === 'past') return isPast(appointment, now)
  if (tab === 'cancelled') return appointment.status === 'CANCELLED'
  return true
}

function AppointmentsPage() {
  const { currentOrgId, currentRole } = useAuth()
  const navigate = useNavigate()
  const searchParams = Route.useSearch()
  const [tab, setTab] = useState<AppointmentTab>('upcoming')
  const [view, setView] = useState<ViewMode>('list')
  const [query, setQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [ownerFilter, setOwnerFilter] = useState<string>('all')
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<Appointment | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<Appointment | null>(null)
  const [defaultDate, setDefaultDate] = useState<Date | null>(null)
  const [form, setForm] = useState(() => emptyForm())

  const isListView = view === 'list'

  // Busca com debounce para a lista (server-side); o input atualiza instantâneo.
  const [debouncedQuery, setDebouncedQuery] = useState('')
  useEffect(() => {
    const id = setTimeout(() => setDebouncedQuery(query), 300)
    return () => clearTimeout(id)
  }, [query])

  const { data: contacts = [], isLoading: contactsLoading } = useContacts(
    currentOrgId,
    dialogOpen
  )
  const { data: schedulingProfiles = [], isLoading: profilesLoading } =
    useSchedulingProfiles(currentOrgId, dialogOpen)
  const activeSchedulingProfiles = useMemo(
    () => schedulingProfiles.filter((profile) => profile.isActive),
    [schedulingProfiles]
  )
  // Lista: paginada/filtrada/ordenada no servidor (infinite scroll).
  const listQuery = useAppointmentsInfinite(
    currentOrgId,
    {
      tab,
      search: debouncedQuery,
      status: statusFilter,
      ownerUserId: ownerFilter,
    },
    isListView
  )
  // Calendário (Semana/Mês): mantém o carregamento único + filtro client-side.
  const { data: calendarResponse, isLoading: calendarLoading } =
    useAppointments(
      currentOrgId,
      { tab: 'all', ownerUserId: ownerFilter },
      !isListView
    )
  // Só quem enxerga a agenda dos outros tem o que filtrar: para o operador o
  // servidor já restringe à agenda dele.
  const canFilterByOwner =
    currentRole === 'OWNER' ||
    currentRole === 'ADMIN' ||
    currentRole === 'MANAGER'
  const { data: orgMembersResponse } = useOrgMembers(
    currentOrgId,
    canFilterByOwner
  )
  const orgMembers = orgMembersResponse?.data ?? []
  const { data: countsData } = useAppointmentCounts(currentOrgId)
  const createAppointment = useCreateAppointment(currentOrgId)
  const updateAppointment = useUpdateAppointment(currentOrgId)
  const updateStatus = useUpdateAppointmentStatus(currentOrgId)
  const deleteAppointment = useDeleteAppointment(currentOrgId)

  const listAppointments = useMemo(
    () => listQuery.data?.pages.flatMap((page) => page.data) ?? [],
    [listQuery.data]
  )
  const calendarAppointments = useMemo(
    () => calendarResponse?.data ?? [],
    [calendarResponse]
  )

  const counts: Record<AppointmentTab, number> = countsData ?? {
    upcoming: 0,
    pending: 0,
    past: 0,
    cancelled: 0,
    all: 0,
  }

  const appointmentsLoading = isListView ? listQuery.isLoading : calendarLoading

  // Calendário: filtro client-side (aba + status + busca instantânea).
  const calendarFiltered = useMemo(() => {
    const term = query.trim().toLowerCase()
    return calendarAppointments
      .filter((appointment) => matchesTab(appointment, tab))
      .filter((appointment) =>
        statusFilter === 'all' ? true : appointment.status === statusFilter
      )
      .filter((appointment) => {
        if (!term) return true
        return (
          appointment.title.toLowerCase().includes(term) ||
          (appointment.description ?? '').toLowerCase().includes(term) ||
          (appointment.location ?? '').toLowerCase().includes(term) ||
          getLeadLabel(appointment).toLowerCase().includes(term)
        )
      })
  }, [calendarAppointments, query, statusFilter, tab])

  // Sentinel do infinite scroll da lista.
  const sentinelRef = useRef<HTMLDivElement | null>(null)
  const { fetchNextPage, hasNextPage, isFetchingNextPage } = listQuery
  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel || !isListView) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
          void fetchNextPage()
        }
      },
      { threshold: 0.1 }
    )
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [fetchNextPage, hasNextPage, isFetchingNextPage, isListView])

  useEffect(() => {
    if (!searchParams.new) return
    openCreate(searchParams.leadId)
  }, [searchParams.leadId, searchParams.new])

  function openCreate(
    defaultLeadId?: string | null,
    scheduledAt?: Date | null
  ) {
    setEditing(null)
    setDefaultDate(scheduledAt ?? null)
    setForm(emptyForm(defaultLeadId, scheduledAt))
    setDialogOpen(true)
  }

  function openEdit(appointment: Appointment) {
    setEditing(appointment)
    setDefaultDate(null)
    setForm({
      title: appointment.title,
      scheduled_at: toLocalInputValue(appointment.scheduled_at),
      duration_minutes: appointment.duration_minutes ?? 30,
      leadId: appointment.leadId,
      schedulingProfileId: appointment.schedulingProfileId ?? '',
      location: appointment.location ?? '',
      meeting_url: appointment.meeting_url ?? '',
      description: appointment.description ?? '',
      status: appointment.status,
    })
    setDialogOpen(true)
  }

  async function submit(event: FormEvent) {
    event.preventDefault()
    if (!form.leadId) {
      toast.error('Selecione o lead')
      return
    }

    const payload: AppointmentInput = {
      title: form.title.trim(),
      leadId: form.leadId,
      schedulingProfileId: form.schedulingProfileId || undefined,
      scheduled_at: new Date(form.scheduled_at).toISOString(),
      duration_minutes: form.duration_minutes,
      location: form.location.trim() || undefined,
      meeting_url: form.meeting_url.trim() || undefined,
      description: form.description.trim() || undefined,
    }

    if (editing) {
      await updateAppointment.mutateAsync({ id: editing.id, data: payload })
      if (form.status !== editing.status) {
        await updateStatus.mutateAsync({ id: editing.id, status: form.status })
      }
      toast.success('Agendamento atualizado')
    } else {
      await createAppointment.mutateAsync(payload)
      toast.success('Agendamento criado')
    }
    setDialogOpen(false)
  }

  async function changeStatus(
    appointment: Appointment,
    status: AppointmentStatus
  ) {
    await updateStatus.mutateAsync({ id: appointment.id, status })
    toast.success('Status atualizado')
  }

  async function confirmDelete() {
    if (!deleteTarget) return
    await deleteAppointment.mutateAsync(deleteTarget.id)
    toast.success('Agendamento excluido')
    setDeleteTarget(null)
  }

  if (!currentOrgId) {
    return (
      <div className="text-muted-foreground flex flex-1 items-center justify-center">
        Selecione uma organização
      </div>
    )
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <PageHeader
        icon={Calendar}
        title="Agendamentos"
        description="Reuniões, visitas e compromissos com leads"
        actions={
          <>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => void navigate({ to: '/agendamentos/perfis' })}
              >
                <SlidersHorizontal className="h-4 w-4" />
                Perfis
              </Button>
              <Button type="button" onClick={() => openCreate()}>
                <Plus className="h-4 w-4" />
                Novo agendamento
              </Button>
            </div>
          </>
        }
      />

      <main className="min-h-0 flex-1 overflow-auto p-6">
        <Tabs
          value={tab}
          onValueChange={(value) => setTab(value as AppointmentTab)}
        >
          <div className="flex flex-wrap items-center justify-between gap-3">
            <TabsList className={cn(SEG_LIST_CLASS, 'flex-wrap')}>
              {TABS.map((item) => (
                <TabsTrigger
                  key={item.value}
                  value={item.value}
                  className={SEG_TRIGGER_CLASS}
                >
                  {item.label}
                  <Badge variant="secondary" className="ml-2 h-5 px-1.5">
                    {counts[item.value]}
                  </Badge>
                </TabsTrigger>
              ))}
            </TabsList>

            <div className="bg-card border-border inline-flex items-center gap-1 rounded-md border p-1">
              {(
                [
                  { key: 'list', label: 'Lista', icon: List },
                  { key: 'week', label: 'Semana', icon: CalendarRange },
                  { key: 'month', label: 'Mês', icon: CalendarDays },
                ] as const
              ).map(({ key, label, icon: Icon }) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setView(key)}
                  className={cn(
                    'inline-flex items-center gap-1.5 rounded px-3 py-1.5 text-sm font-medium transition-colors',
                    view === key
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  )}
                >
                  <Icon className="h-3.5 w-3.5" />
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <div className="relative min-w-64 flex-1">
              <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
              <Input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Buscar por lead, título, local..."
                className="pl-9"
              />
            </div>
            <Select
              value={statusFilter}
              onValueChange={(value) => setStatusFilter(value as StatusFilter)}
            >
              <SelectTrigger className="w-full sm:w-60">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os status</SelectItem>
                {Object.entries(STATUS_LABEL).map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {canFilterByOwner && (
              <Select value={ownerFilter} onValueChange={setOwnerFilter}>
                <SelectTrigger className="w-full sm:w-60">
                  <SelectValue placeholder="Responsável" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os responsáveis</SelectItem>
                  {orgMembers.map((member) => (
                    <SelectItem key={member.userId} value={member.userId}>
                      {member.user.name ?? member.user.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          <TabsContent value={tab} className="mt-4">
            {appointmentsLoading ? (
              <AppointmentSkeleton />
            ) : isListView ? (
              listAppointments.length === 0 ? (
                <EmptyAppointments onCreate={() => openCreate()} />
              ) : (
                <>
                  <AppointmentsTable
                    appointments={listAppointments}
                    onEdit={openEdit}
                    onDelete={setDeleteTarget}
                    onChangeStatus={changeStatus}
                  />
                  <div ref={sentinelRef} className="h-1" />
                  {isFetchingNextPage ? (
                    <div className="text-muted-foreground flex items-center justify-center gap-2 py-4 text-sm">
                      <Spinner />
                      Carregando mais...
                    </div>
                  ) : null}
                </>
              )
            ) : (
              <AppointmentsCalendar
                view={view}
                appointments={calendarFiltered}
                onSelectAppointment={openEdit}
                onCreateAt={(date) => openCreate(null, date)}
              />
            )}
          </TabsContent>
        </Tabs>
      </main>

      <AppointmentDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editing={editing}
        form={form}
        setForm={setForm}
        contacts={contacts}
        contactsLoading={contactsLoading}
        schedulingProfiles={activeSchedulingProfiles}
        profilesLoading={profilesLoading}
        defaultDate={defaultDate}
        saving={createAppointment.isPending || updateAppointment.isPending}
        onSubmit={submit}
        onOpenLead={(leadId) => {
          setDialogOpen(false)
          void navigate({ to: '/leads/$leadId', params: { leadId } })
        }}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title="Excluir agendamento?"
        description="Esta ação remove o compromisso da agenda."
        confirmLabel="Excluir"
        destructive
        pending={deleteAppointment.isPending}
        onConfirm={() => void confirmDelete()}
      />
    </div>
  )
}

function AppointmentSkeleton() {
  return (
    <div className="space-y-2">
      {Array.from({ length: 6 }).map((_, index) => (
        <Skeleton key={index} className="h-16 w-full rounded-md" />
      ))}
    </div>
  )
}

function EmptyAppointments({ onCreate }: { onCreate: () => void }) {
  return (
    <EmptyState
      icon={Calendar}
      title="Nenhum agendamento"
      description="Crie uma reunião, visita ou compromisso vinculado a um lead."
      action={
        <Button type="button" variant="outline" size="sm" onClick={onCreate}>
          <Plus className="h-3.5 w-3.5" />
          Criar agendamento
        </Button>
      }
    />
  )
}

function AppointmentsTable({
  appointments,
  onEdit,
  onDelete,
  onChangeStatus,
}: {
  appointments: Appointment[]
  onEdit: (appointment: Appointment) => void
  onDelete: (appointment: Appointment) => void
  onChangeStatus: (appointment: Appointment, status: AppointmentStatus) => void
}) {
  return (
    <div className="bg-card overflow-hidden rounded-lg border">
      <Table className="[&_td]:px-4 [&_td]:py-3.5 [&_td:first-child]:pl-6 [&_td:last-child]:pr-6 [&_th]:h-11 [&_th]:px-4 [&_th:first-child]:pl-6 [&_th:last-child]:pr-6">
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead>Título</TableHead>
            <TableHead>Lead</TableHead>
            <TableHead>Quando</TableHead>
            <TableHead>Perfil / responsável</TableHead>
            <TableHead>Duração</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="w-12" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {appointments.map((appointment) => (
            <TableRow
              key={appointment.id}
              className="cursor-pointer"
              onClick={() => onEdit(appointment)}
            >
              <TableCell>
                <div className="font-medium">{appointment.title}</div>
                {appointment.location ? (
                  <div className="text-muted-foreground mt-0.5 flex items-center gap-1 text-xs">
                    <MapPin className="h-3 w-3" />
                    {appointment.location}
                  </div>
                ) : null}
                {appointment.meeting_url ? (
                  <div className="text-muted-foreground mt-0.5 flex max-w-80 items-center gap-1 truncate text-xs">
                    <LinkIcon className="h-3 w-3 shrink-0" />
                    <span className="truncate">{appointment.meeting_url}</span>
                  </div>
                ) : null}
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-1.5 text-sm">
                  <User className="text-muted-foreground h-3.5 w-3.5" />
                  {getLeadLabel(appointment)}
                </div>
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-1.5 text-sm">
                  <Clock className="text-muted-foreground h-3.5 w-3.5" />
                  {formatDateTime(appointment.scheduled_at)}
                </div>
              </TableCell>
              <TableCell>
                <div className="text-sm">
                  {appointment.schedulingProfile?.name ?? 'Sem perfil'}
                </div>
                <div className="text-muted-foreground mt-0.5 text-xs">
                  {appointment.assignedUser?.name ??
                    appointment.schedulingProfile?.ownerUser?.name ??
                    appointment.schedulingProfile?.ownerUser?.email ??
                    appointment.creatorUser?.name ??
                    'Sem responsável'}
                </div>
              </TableCell>
              <TableCell className="text-muted-foreground text-sm">
                {appointment.duration_minutes ?? 30} min
              </TableCell>
              <TableCell>
                <StatusBadge tone={APPOINTMENT_STATUS_TONE[appointment.status]}>
                  {STATUS_LABEL[appointment.status]}
                </StatusBadge>
              </TableCell>
              <TableCell onClick={(event) => event.stopPropagation()}>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button type="button" variant="ghost" size="icon">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {(STATUS_ACTIONS[appointment.status] ?? []).map(
                      (action) => {
                        const Icon = action.icon
                        return (
                          <DropdownMenuItem
                            key={action.status}
                            onClick={() =>
                              onChangeStatus(appointment, action.status)
                            }
                          >
                            <Icon className="mr-2 h-4 w-4" />
                            {action.label}
                          </DropdownMenuItem>
                        )
                      }
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      className="text-destructive focus:text-destructive"
                      onClick={() => onDelete(appointment)}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Excluir
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}

function AppointmentDialog({
  open,
  onOpenChange,
  editing,
  form,
  setForm,
  contacts,
  contactsLoading,
  schedulingProfiles,
  profilesLoading,
  defaultDate,
  saving,
  onSubmit,
  onOpenLead,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  editing: Appointment | null
  form: ReturnType<typeof emptyForm>
  setForm: Dispatch<SetStateAction<ReturnType<typeof emptyForm>>>
  contacts: ContactListItem[]
  contactsLoading: boolean
  schedulingProfiles: SchedulingProfile[]
  profilesLoading: boolean
  defaultDate: Date | null
  saving: boolean
  onSubmit: (event: FormEvent) => void
  onOpenLead: (leadId: string) => void
}) {
  const originLabel = {
    MANUAL: 'Manual',
    AI_AGENT: 'Agente de IA',
    CADENCE: 'Cadência',
    API: 'API',
  }[editing?.origin ?? 'MANUAL']
  const syncLabel = {
    PENDING: 'Sincronização pendente',
    SYNCED: 'Google sincronizado',
    FAILED: 'Falha no Google',
    SKIPPED: 'Agenda interna',
    DELETED: 'Evento removido',
  }[editing?.google_sync_status ?? 'PENDING']
  const responsible =
    editing?.assignedUser?.name ??
    editing?.schedulingProfile?.ownerUser?.name ??
    editing?.schedulingProfile?.ownerUser?.email ??
    editing?.creatorUser?.name ??
    'Sem responsável'
  const selectedProfile = schedulingProfiles.find(
    (profile) => profile.id === form.schedulingProfileId
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {editing ? 'Editar agendamento' : 'Novo agendamento'}
          </DialogTitle>
        </DialogHeader>
        <form className="space-y-4" onSubmit={onSubmit}>
          {editing ? (
            <div className="bg-muted/30 rounded-lg border p-3">
              <div className="grid gap-3 text-xs sm:grid-cols-2">
                <div>
                  <p className="text-muted-foreground">Origem</p>
                  <p className="mt-0.5 font-medium">{originLabel}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Sincronização</p>
                  <p className="mt-0.5 font-medium">{syncLabel}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Perfil</p>
                  <p className="mt-0.5 font-medium">
                    {editing.schedulingProfile?.name ?? 'Sem perfil'}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground">Responsável</p>
                  <p className="mt-0.5 font-medium">{responsible}</p>
                </div>
              </div>
              {editing.google_last_sync_error ? (
                <p className="text-destructive mt-2 text-xs">
                  {editing.google_last_sync_error}
                </p>
              ) : null}
              <div className="mt-3 flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => onOpenLead(editing.leadId)}
                >
                  <User className="size-3.5" />
                  Abrir lead
                </Button>
                {editing.meeting_url ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      window.open(
                        editing.meeting_url ?? '',
                        '_blank',
                        'noopener,noreferrer'
                      )
                    }
                  >
                    <LinkIcon className="size-3.5" />
                    Abrir reunião
                  </Button>
                ) : null}
              </div>
            </div>
          ) : null}
          <div className="space-y-2">
            <Label htmlFor="appointment-title">Título *</Label>
            <Input
              id="appointment-title"
              value={form.title}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  title: event.target.value,
                }))
              }
              placeholder="Reunião de apresentação"
              required
            />
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="appointment-date">Data e hora *</Label>
              <Input
                id="appointment-date"
                type="datetime-local"
                value={form.scheduled_at}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    scheduled_at: event.target.value,
                  }))
                }
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="appointment-duration">Duração (min)</Label>
              <Input
                id="appointment-duration"
                type="number"
                min={5}
                step={5}
                value={form.duration_minutes}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    duration_minutes: Number(event.target.value) || 30,
                  }))
                }
              />
            </div>
          </div>

          {defaultDate ? (
            <div className="border-primary/30 bg-primary/5 text-primary rounded-md border px-3 py-2 text-xs">
              Criado a partir do calendário em{' '}
              {formatDateTime(defaultDate.toISOString())}.
            </div>
          ) : null}

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Lead *</Label>
              {editing ? (
                <div className="bg-muted/40 flex h-9 items-center rounded-md border px-3 text-sm">
                  {editing.lead?.name ??
                    editing.lead?.phone ??
                    editing.lead?.email ??
                    'Lead'}
                </div>
              ) : (
                <Select
                  value={form.leadId}
                  onValueChange={(leadId) =>
                    setForm((current) => ({ ...current, leadId }))
                  }
                  disabled={contactsLoading}
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={
                        contactsLoading
                          ? 'Carregando leads...'
                          : 'Selecione o lead'
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {contacts.map((contact) => (
                      <SelectItem key={contact.id} value={contact.id}>
                        {contact.name ?? contact.phone ?? contact.id}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            {editing ? (
              <div className="space-y-2">
                <Label>Status</Label>
                <Select
                  value={form.status}
                  onValueChange={(status) =>
                    setForm((current) => ({
                      ...current,
                      status: status as AppointmentStatus,
                    }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[
                      editing.status,
                      ...(STATUS_ACTIONS[editing.status] ?? []).map(
                        (action) => action.status
                      ),
                    ].map((status) => (
                      <SelectItem key={status} value={status}>
                        {STATUS_LABEL[status]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div className="rounded-md border px-3 py-2">
                <p className="text-sm font-medium">Status inicial</p>
                <p className="text-muted-foreground text-xs">
                  Novos agendamentos entram como solicitado.
                </p>
              </div>
            )}
          </div>

          {!editing ? (
            <div className="space-y-2">
              <Label>Perfil de agendamento</Label>
              <Select
                value={form.schedulingProfileId || 'none'}
                onValueChange={(profileId) => {
                  const nextProfile =
                    profileId === 'none'
                      ? null
                      : schedulingProfiles.find(
                          (profile) => profile.id === profileId
                        )
                  setForm((current) => ({
                    ...current,
                    schedulingProfileId: nextProfile?.id ?? '',
                    duration_minutes:
                      nextProfile?.durationMinutes ?? current.duration_minutes,
                  }))
                }}
                disabled={profilesLoading}
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={
                      profilesLoading
                        ? 'Carregando perfis...'
                        : 'Selecione um perfil'
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sem perfil</SelectItem>
                  {schedulingProfiles.map((profile) => (
                    <SelectItem key={profile.id} value={profile.id}>
                      {profile.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-muted-foreground text-xs">
                {selectedProfile?.ownerUser
                  ? `Sincroniza no Google Calendar de ${selectedProfile.ownerUser.name ?? selectedProfile.ownerUser.email}.`
                  : selectedProfile
                    ? 'Sincroniza no Google Calendar da organização.'
                    : 'Sem perfil, o agendamento usa apenas as regras manuais e o calendário da organização.'}
              </p>
            </div>
          ) : null}

          <div className="space-y-2">
            <Label htmlFor="appointment-location">Local</Label>
            <Input
              id="appointment-location"
              value={form.location}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  location: event.target.value,
                }))
              }
              placeholder="Google Meet, telefone, escritório..."
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="appointment-meeting-url">Link da reunião</Label>
            <Input
              id="appointment-meeting-url"
              value={form.meeting_url}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  meeting_url: event.target.value,
                }))
              }
              placeholder="https://meet.google.com/..."
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="appointment-notes">Notas internas</Label>
            <Textarea
              id="appointment-notes"
              value={form.description}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  description: event.target.value,
                }))
              }
              rows={3}
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={saving || !form.leadId}>
              {saving ? <Spinner /> : null}
              Salvar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
